import { useMemo } from 'react';
import { Alert, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { FieldGeometry } from 'base/types/RecommendationCropwise';
import { SoilType } from 'base/types/SoilType';
import { getSoilClayPercentage } from 'utils/constants/SoilColors';
import { getSoilBreakdown } from './getSoilBreakdown';
import {
  SoilDistribution,
  SoilSegment,
  SoilLegend,
  SoilLegendRow,
  SoilSwatch,
  SoilDescription,
  SoilQuantity,
  SoilClay,
} from './FieldConditions.styles';

interface SoilBreakdownProps {
  cells: SoilType[];
  resolution: number;
  geometry: FieldGeometry;
  isBrazil?: boolean;
}

interface SoilClayEstimateProps {
  texture: string;
}

export function SoilClayEstimate({ texture }: SoilClayEstimateProps): JSX.Element | null {
  const { t } = useTranslation();
  const percentage = getSoilClayPercentage(texture);
  if (percentage === null) {
    return null;
  }
  return (
    <Tooltip title={t('Estimated clay content based on soil texture')}>
      <SoilClay>
        {t('{{percentage}}% Clay', { percentage: String(percentage).padStart(2, '0') })}
      </SoilClay>
    </Tooltip>
  );
}

export default function SoilBreakdown({
  cells,
  resolution,
  geometry,
  isBrazil = false,
}: SoilBreakdownProps) {
  const { t } = useTranslation();
  const result = useMemo(() => {
    try {
      return { items: getSoilBreakdown(cells, resolution, geometry), error: false };
    } catch (error: unknown) {
      return { items: [], error: true };
    }
  }, [cells, resolution, geometry]);

  if (result.error) {
    return <Alert type="warning" message={t('Soil distribution could not be calculated')} />;
  }
  if (!result.items.length) {
    return <span>{t('No soil data available for this field')}</span>;
  }

  return (
    <SoilLegend>
      <SoilDistribution aria-hidden="true" data-testid="soil-distribution">
        {result.items.map((item) => (
          <SoilSegment key={item.key} $color={item.color} $percentage={item.percentage} />
        ))}
      </SoilDistribution>
      <ul aria-label={t('Soil distribution')}>
        {result.items.map((item) => (
          <SoilLegendRow key={item.key}>
            <SoilSwatch $color={item.color} aria-hidden="true" />
            <SoilDescription>
              <strong>{t(item.label)}</strong>
              <small> · {t(item.category)}</small>
            </SoilDescription>
            <SoilQuantity>
              {item.hectares.toLocaleString(undefined, { maximumFractionDigits: 2 })} ha ·{' '}
              {item.percentage.toLocaleString(undefined, { maximumFractionDigits: 1 })}%
            </SoilQuantity>
            {isBrazil && <SoilClayEstimate texture={item.key} />}
          </SoilLegendRow>
        ))}
      </ul>
    </SoilLegend>
  );
}
